import { Box, Typography, useTheme } from "@mui/material";
import { DataListOption, FieldValue, useActionFieldInput } from "src/api/client-api/ActionPlan/ActionPlanAction";
import { Loading } from "src/components/utils/Loading";
import { useTranslation } from "react-i18next";
import { ActionParameterInput } from "src/components/Actions/Common/ActionParameterInput";
import { useActionsContext } from "src/context/Actions/useActionsContext";
import { useMyActionPlanContext } from "../../../../context/useMyActionPlan";
import { useLanguage } from "src/context/LanguageProvider";
import { useEntityScores } from "src/context/Scores/useEntityScores";

type ActionFieldInputComponentProps = {
    fieldValue: FieldValue | undefined;
    dataListOptions: DataListOption[] | undefined;
    fieldType: string;
    unit: string | undefined;
    disabled: boolean;
    position: number;
    actionFieldId: number;
};

export const ActionFieldInputComponent = (props: ActionFieldInputComponentProps) => {
    const { disabled, fieldValue, fieldType, unit, dataListOptions } = props;
    const { t } = useTranslation();
    const { currentLanguage } = useLanguage();
    const { lastSelectedVariation } = useEntityScores();
    const theme = useTheme();
    const { actionPlan, isFieldValueUpdating, handleValidationError } = useActionsContext();
    const { actionPlanAction, isFieldValueReady } = useMyActionPlanContext();

    const fieldInput = useActionFieldInput({
        actionPlanId: actionPlan?.id || "",
        actionPlanActionId: actionPlanAction.id,
        fieldValue: fieldValue,
        position: props.position,
        actionFieldId: props.actionFieldId,
        onSuccess: () => {
            console.info(`Field ${fieldValue?.id} saved successfully!`);
        },
        onError: (error) => {
            console.error(`Error saving field ${fieldValue?.id}:`, error);
        },
        onValidationError: handleValidationError,
    });

    const isDisabled = disabled || isFieldValueUpdating;

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flex: 1,
            }}
        >
            {isFieldValueReady && (
                <ActionParameterInput
                    value={fieldInput.value}
                    codes={fieldInput.dataListOptionCodes}
                    onChange={fieldInput.onChange}
                    onOptionsChange={fieldInput.onDataListChange}
                    onBlur={fieldInput.onBlur}
                    locale={currentLanguage}
                    precision={lastSelectedVariation.precision}
                    options={dataListOptions}
                    type={fieldType}
                    unit={unit}
                    disabled={isDisabled}
                />
            )}
            {unit && (
                <Typography
                    variant="big-medium"
                    sx={{
                        lineHeight: "1.6em",
                        color: "#393A36",
                    }}
                >
                    {unit}
                </Typography>
            )}
            {fieldInput.isSaving && <Loading size={parseInt(theme.spacing(4))} sx={{ margin: 0 }} />}
            {fieldInput.hasError && (
                <Typography variant="small" sx={{ color: "#f44336" }}>
                    {t("action_plan.action_fields.error", "Error on saving the field value")}
                </Typography>
            )}
        </Box>
    );
};
